import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { isApiError, KmiApiError } from '../api/client';
import {
  assignClientTeamMember,
  endClientTeamAssignment,
  listClients,
  listRoles,
  listUserInvitations,
  listUserRoles,
  revokeUserInvitation,
  sendUserInvitation,
} from '../api/endpoints';
import type {
  ClientRow,
  InvitationStatus,
  RoleRow,
  UserInvitationRow,
  UserRoleRow,
} from '../api/types';
import { EmptyState, ErrorState, LoadingState } from '../components/states';
import { useMeContext } from '../context/MeContextProvider';
import { hasAnyPermission, isClientOnly, isLeadership, PERM } from '../permissions';

type LoadState =
  | { readonly status: 'loading' }
  | {
      readonly status: 'ready';
      readonly roles: ReadonlyArray<RoleRow>;
      readonly clients: ReadonlyArray<ClientRow>;
      readonly invitations: ReadonlyArray<UserInvitationRow>;
      readonly userRoles: ReadonlyArray<UserRoleRow>;
    }
  | { readonly status: 'error'; readonly error: unknown };

interface CreatedInvitation {
  readonly email: string;
  readonly url: string;
  readonly expires_at: string;
}

const ROLE_PRIORITY = ['client_admin', 'client_manager', 'client_approver', 'client_viewer'];

/**
 * Role keys a client-only actor (e.g. `client_admin`) is allowed to issue
 * invitations for. Excludes `client_admin` itself to prevent a
 * client-portal user from minting another full-rights peer, and excludes
 * `client_approver` until product explicitly asks for it. RLS on the
 * insert is the load-bearing gate; this allowlist is UX-only.
 */
const CLIENT_ONLY_ALLOWED_ROLE_KEYS: ReadonlyArray<string> = ['client_manager', 'client_viewer'];

const INVITATION_STATUS_LABEL: Record<InvitationStatus, string> = {
  pending: 'قيد الانتظار',
  accepted: 'مقبولة',
  expired: 'منتهية',
  revoked: 'ملغاة',
};

/**
 * Task 112 (TEAM-1B) — FE-side cap on the optional CAT-bootstrap
 * selection. Matches the EF cap; the EF re-validates. The UI is just
 * a soft guard with a clearer message.
 */
const MAX_INTENDED_CLIENT_ASSIGNMENTS = 50;

export function TeamStubPage() {
  const { me } = useMeContext();
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  const [reloadKey, setReloadKey] = useState(0);
  const [clientFilter, setClientFilter] = useState<string>('all');
  const [inviteScope, setInviteScope] = useState<'client' | 'agency'>('agency');
  const [email, setEmail] = useState('');
  const [roleId, setRoleId] = useState('');
  const [clientId, setClientId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [createdInvite, setCreatedInvite] = useState<CreatedInvitation | null>(null);
  const [copied, setCopied] = useState(false);
  // Task 112 — optional CAT-bootstrap selection for agency invitations.
  // The set is reset whenever the scope flips away from 'agency' or the
  // role changes to a non-agency role.
  const [selectedClientAgencyTeamIds, setSelectedClientAgencyTeamIds] = useState<
    ReadonlySet<string>
  >(new Set());

  const permissions = me?.permissions ?? [];
  const clientOnly = me ? isClientOnly(me) : false;
  /**
   * Task 127 — leadership gate for the per-employee detail drawer that
   * lets a leader manage which clients an existing agency employee is
   * assigned to. Mirrors `ClientTeamPage`'s `isLeader` gate. The
   * existing TeamStubPage surface (invitations, roster cards) stays
   * available to non-leadership readers as before; only the new
   * action surface (the detail drawer + the per-client toggle) is
   * leadership-only. RLS on `client_assignments_insert` /
   * `client_assignments_update` + the EF-side `assertCallerIsLeadership`
   * preflight are the load-bearing security gates.
   */
  const isLeader = !!me && !me.system_admin && isLeadership(me);
  /**
   * Task 127 — which employee's detail drawer is currently open.
   * Null = no drawer; otherwise the auth_user_id of the selected
   * agency employee. Cleared when the leader clicks "back to roster",
   * when the page reloads, or when the selected employee falls off
   * the roster (e.g. after an inactive-profile transition).
   */
  const [selectedEmployeeUserId, setSelectedEmployeeUserId] = useState<string | null>(null);
  /**
   * Task 127 — per-(employee × client) toggle state used by the
   * detail drawer's clients checklist. The key is `${userId}::${clientId}`;
   * the value is a tiny state machine for one row. Disables the
   * row's checkbox while a request is in flight and surfaces an
   * Arabic error if the EF rejects the toggle.
   */
  interface ToggleRowState {
    readonly busy: boolean;
    readonly error: string | null;
  }
  const [toggleState, setToggleState] = useState<ReadonlyMap<string, ToggleRowState>>(new Map());
  /**
   * Cross-org read gate. Only `team.read` (leadership) is permitted to
   * fan out across the whole organization — that's what the
   * `list-user-roles` runtime requires. `invitation.read` alone is NOT
   * sufficient: `list-user-roles` rejects `client_id=undefined` from
   * non-`team.read` callers with `validation_error:
   * required_for_client_scoped_reader`, which is the launch bug this
   * change fixes. Aligning the frontend gate with the backend contract
   * keeps the team page from sending a payload the server can never
   * accept.
   */
  const canReadAcrossOrg = hasAnyPermission(permissions, PERM.TEAM_READ);
  const canInvite = hasAnyPermission(
    permissions,
    PERM.INVITATION_SEND,
    PERM.INVITATION_CLIENT_USER_MANAGE,
  );
  const canRevoke = hasAnyPermission(
    permissions,
    PERM.INVITATION_REVOKE,
    PERM.INVITATION_CLIENT_USER_MANAGE,
  );
  const fallbackClientId = me?.client_assignments[0]?.client_id ?? '';
  const effectiveClientFilter = canReadAcrossOrg
    ? clientFilter === 'all'
      ? undefined
      : clientFilter
    : fallbackClientId || undefined;

  useEffect(() => {
    if (!me) return;
    if (!canReadAcrossOrg && fallbackClientId) {
      setClientFilter(fallbackClientId);
      setClientId(fallbackClientId);
    }
  }, [canReadAcrossOrg, fallbackClientId, me]);

  // Keep this page role-scoped: agency leaders invite agency members here;
  // client-only actors invite client-side users from their client team page.
  useEffect(() => {
    const nextScope = clientOnly ? 'client' : 'agency';
    if (inviteScope !== nextScope) setInviteScope(nextScope);
  }, [clientOnly, inviteScope]);

  // Task 112 — clear the CAT-bootstrap selection whenever the scope
  // flips away from agency. Prevents leaking stale picks when the user
  // switches from inviting an agency member to inviting a client user.
  useEffect(() => {
    if (inviteScope !== 'agency' && selectedClientAgencyTeamIds.size > 0) {
      setSelectedClientAgencyTeamIds(new Set());
    }
  }, [inviteScope, selectedClientAgencyTeamIds.size]);

  // True when the actor is client-scoped but has no resolvable client_id.
  // Calling `list-user-roles` in that state would trigger
  // `validation_error: required_for_client_scoped_reader` and crash the
  // page, so we render a deferred empty state instead.
  const cannotReadInScope = !canReadAcrossOrg && !fallbackClientId;

  useEffect(() => {
    if (!me) return;
    const ctrl = new AbortController();
    setState({ status: 'loading' });
    if (cannotReadInScope) {
      // Still fetch roles/clients so the invite form can resolve a role
      // id if and when an assignment becomes available, but skip the
      // client-scoped lists that the runtime would reject.
      Promise.all([listRoles({ signal: ctrl.signal }), listClients({ signal: ctrl.signal })])
        .then(([rolesData, clientsData]) => {
          if (ctrl.signal.aborted) return;
          setState({
            status: 'ready',
            roles: rolesData.roles,
            clients: clientsData.clients,
            invitations: [],
            userRoles: [],
          });
        })
        .catch((err: unknown) => {
          if (ctrl.signal.aborted) return;
          if (err instanceof DOMException && err.name === 'AbortError') return;
          setState({ status: 'error', error: err });
        });
      return () => ctrl.abort();
    }
    Promise.all([
      listRoles({ signal: ctrl.signal }),
      listClients({ signal: ctrl.signal }),
      listUserInvitations({ clientId: effectiveClientFilter, signal: ctrl.signal }),
      listUserRoles({ clientId: effectiveClientFilter, signal: ctrl.signal }),
    ])
      .then(([rolesData, clientsData, invitationsData, userRolesData]) => {
        if (ctrl.signal.aborted) return;
        setState({
          status: 'ready',
          roles: rolesData.roles,
          clients: clientsData.clients,
          invitations: invitationsData.invitations,
          userRoles: userRolesData.user_roles,
        });
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setState({ status: 'error', error: err });
      });
    return () => ctrl.abort();
  }, [cannotReadInScope, effectiveClientFilter, me, reloadKey]);

  const roles = state.status === 'ready' ? state.roles : [];
  const clients = state.status === 'ready' ? state.clients : [];
  const invitations = state.status === 'ready' ? state.invitations : [];
  const userRoles = state.status === 'ready' ? state.userRoles : [];
  const visibleInvitations = useMemo(() => {
    if (clientOnly) return invitations;
    return invitations.filter((inv) => inv.role?.category === 'agency');
  }, [clientOnly, invitations]);

  const roleOptions = useMemo(() => {
    return roles
      .filter((r) => r.category === inviteScope)
      .filter((r) => (clientOnly ? CLIENT_ONLY_ALLOWED_ROLE_KEYS.includes(r.key) : true))
      .sort((a, b) => {
        const ai = ROLE_PRIORITY.indexOf(a.key);
        const bi = ROLE_PRIORITY.indexOf(b.key);
        if (ai !== -1 || bi !== -1) return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
        return roleLabel(a).localeCompare(roleLabel(b), 'ar');
      });
  }, [clientOnly, inviteScope, roles]);

  useEffect(() => {
    const first = roleOptions[0]?.id ?? '';
    if (!roleOptions.some((r) => r.id === roleId)) setRoleId(first);
  }, [roleId, roleOptions]);

  useEffect(() => {
    if (clientId || clients.length === 0) return;
    setClientId(effectiveClientFilter ?? clients[0]?.id ?? '');
  }, [clientId, clients, effectiveClientFilter]);

  async function onInvite(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setFormError(null);
    setCopied(false);
    setCreatedInvite(null);

    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedEmail) {
      setFormError('أدخل بريد المستخدم.');
      return;
    }
    const role = roles.find((r) => r.id === roleId);
    if (!role) {
      setFormError('اختر دوراً صالحاً.');
      return;
    }
    const targetClientId = role.category === 'client' ? clientId : null;
    if (role.category === 'client' && !targetClientId) {
      setFormError('اختر العميل الذي سيملك هذا المستخدم صلاحية عليه.');
      return;
    }

    // Task 112 — agency-category invites carry an OPTIONAL set of CAT
    // memberships in `client_ids`. The EF rejects this field on
    // non-agency invitations, so we only pass it for agency.
    const agencyClientIds =
      role.category === 'agency' && selectedClientAgencyTeamIds.size > 0
        ? Array.from(selectedClientAgencyTeamIds)
        : undefined;
    if (agencyClientIds && agencyClientIds.length > MAX_INTENDED_CLIENT_ASSIGNMENTS) {
      setFormError(
        `لا يمكن تعيين أكثر من ${MAX_INTENDED_CLIENT_ASSIGNMENTS} عميل دفعة واحدة في الدعوة.`,
      );
      return;
    }

    setSubmitting(true);
    try {
      const result = await sendUserInvitation({
        input: {
          email: trimmedEmail,
          role_id: role.id,
          client_id: targetClientId,
          ...(agencyClientIds ? { client_ids: agencyClientIds } : {}),
        },
      });
      const url = buildInvitationUrl(result.raw_token, trimmedEmail);
      setCreatedInvite({ email: trimmedEmail, url, expires_at: result.expires_at });
      setEmail('');
      // Task 112 — reset the CAT-bootstrap selection after a successful
      // invite so the next invite starts clean.
      setSelectedClientAgencyTeamIds(new Set());
      setReloadKey((n) => n + 1);
    } catch (err) {
      if (isApiError(err)) {
        if (err.code === 'permission_denied') {
          setFormError('لا تملك صلاحية إنشاء هذه الدعوة.');
        } else if (err.code === 'validation_error') {
          setFormError('بيانات الدعوة غير صالحة. تأكد من البريد والدور والعميل.');
        } else if (err.code === 'conflict') {
          setFormError('توجد دعوة معلقة أو تعيين قائم لهذا المستخدم.');
        } else {
          setFormError('تعذر إنشاء الدعوة. حاول مرة أخرى.');
        }
      } else {
        setFormError('تعذر إنشاء الدعوة. حاول مرة أخرى.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  /**
   * Task 127 — toggle a (employee × client) assignment from the
   * detail drawer's clients checklist. Adds via
   * `assign-client-team-member` when going checked, removes via
   * `end-client-team-assignment` when going unchecked. On success,
   * triggers the page-level reload so both the drawer's checklist
   * AND the roster card's pills reflect the new state. On error,
   * surfaces an Arabic message inline on the row and leaves the
   * checkbox in its pre-toggle state (caller does optimistic
   * read but does NOT optimistically mutate the page-level state).
   *
   * The function intentionally only writes via `assign-client-team-member`
   * + `end-client-team-assignment` — both Task 109 EFs, both deployed
   * v2 on production. NO invitation, NO user_roles mutation, NO
   * user_custom_permissions write.
   */
  async function onToggleClientAssignment(
    userId: string,
    clientId: string,
    currentAssignmentId: string | null,
  ): Promise<void> {
    const key = `${userId}::${clientId}`;
    setToggleState((prev) => {
      const next = new Map(prev);
      next.set(key, { busy: true, error: null });
      return next;
    });
    try {
      if (currentAssignmentId === null) {
        // Not currently assigned → assign.
        await assignClientTeamMember({
          input: { client_id: clientId, target_user_id: userId },
        });
      } else {
        // Currently assigned → end (soft-delete).
        await endClientTeamAssignment({
          input: { assignment_id: currentAssignmentId },
        });
      }
      // Trigger the page-level reload. The drawer stays open if the
      // selected employee is still in the refreshed roster (almost
      // always the case — assignment changes don't move employees in
      // or out of the agency category).
      setToggleState((prev) => {
        const next = new Map(prev);
        next.delete(key);
        return next;
      });
      setReloadKey((n) => n + 1);
    } catch (err) {
      setToggleState((prev) => {
        const next = new Map(prev);
        next.set(key, { busy: false, error: toggleErrorMessage(err) });
        return next;
      });
    }
  }

  async function onRevoke(invitationId: string) {
    setFormError(null);
    try {
      await revokeUserInvitation({ input: { invitation_id: invitationId } });
      setReloadKey((n) => n + 1);
    } catch (err) {
      if (isApiError(err) && err.code === 'permission_denied') {
        setFormError('لا تملك صلاحية إلغاء هذه الدعوة.');
      } else {
        setFormError('تعذر إلغاء الدعوة.');
      }
    }
  }

  async function copyInviteUrl() {
    if (!createdInvite) return;
    await navigator.clipboard.writeText(createdInvite.url);
    setCopied(true);
  }

  return (
    <div className="stack-md">
      <div className="page-head team-page-head">
        <div>
          <h1 className="h-page">{clientOnly ? 'فريق العميل' : 'الفريق والصلاحيات'}</h1>
          <p className="sub">
            {clientOnly
              ? 'ادعُ أعضاء فريق العميل لمشاركتك في هذه المساحة. حالياً لا يوجد إرسال بريد تلقائي، لذلك يظهر رابط الدعوة مرة واحدة لتنسخه وترسله يدوياً.'
              : 'ادعُ أعضاء فريق الوكالة فقط. دعوات مستخدمي العملاء تتم من صفحة العملاء حتى تبقى مرتبطة بالعميل الصحيح.'}
          </p>
        </div>
        {canReadAcrossOrg && clients.length > 0 ? (
          <label className="team-filter">
            <span>نطاق العرض</span>
            <select value={clientFilter} onChange={(e) => setClientFilter(e.currentTarget.value)}>
              <option value="all">كل الوكالة</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
        ) : null}
      </div>

      {formError ? <div className="banner banner-error">{formError}</div> : null}

      {state.status === 'loading' ? <LoadingState title="جاري تحميل الفريق والدعوات..." /> : null}
      {state.status === 'error' ? (
        <ErrorState
          title="تعذر تحميل الفريق"
          error={state.error}
          onRetry={() => setReloadKey((n) => n + 1)}
        />
      ) : null}

      {state.status === 'ready' ? (
        <>
          {canInvite ? (
            <section className="card team-invite-card">
              <div className="sec-title">
                <div>
                  <h2>إنشاء دعوة</h2>
                  <p className="tiny">
                    {clientOnly
                      ? 'اختر دور العضو داخل فريق العميل: منسق أو مشاهد.'
                      : 'اختر دور الموظف داخل الوكالة، ويمكنك تعيين العملاء الذين سيصل إليهم من نفس الدعوة.'}
                  </p>
                </div>
              </div>

              <form className="team-invite-form" onSubmit={onInvite}>
                <label>
                  <span>البريد الإلكتروني</span>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.currentTarget.value)}
                    placeholder={clientOnly ? 'client@example.com' : 'employee@example.com'}
                    disabled={submitting}
                  />
                </label>
                <label>
                  <span>الدور</span>
                  <select
                    value={roleId}
                    onChange={(e) => setRoleId(e.currentTarget.value)}
                    disabled={submitting || roleOptions.length === 0}
                  >
                    {roleOptions.map((r) => (
                      <option key={r.id} value={r.id}>
                        {roleLabel(r)}
                      </option>
                    ))}
                  </select>
                </label>
                {inviteScope === 'client' && !clientOnly ? (
                  <label>
                    <span>العميل</span>
                    <select
                      value={clientId}
                      onChange={(e) => setClientId(e.currentTarget.value)}
                      disabled={submitting || clients.length === 0 || !canReadAcrossOrg}
                    >
                      {clients.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}
                {/*
                  Task 112 (TEAM-1B) — optional Client Agency Team
                  multi-select for agency invitations. Empty selection
                  is fine (the invitee joins as an agency seat without
                  any CAT memberships; leadership can add them later
                  from the client workspace "الفريق" tab). The EF
                  enforces the same cap.
                */}
                {inviteScope === 'agency' && !clientOnly && clients.length > 0 ? (
                  <AgencyClientAssignmentsPicker
                    clients={clients}
                    selected={selectedClientAgencyTeamIds}
                    onChange={setSelectedClientAgencyTeamIds}
                    disabled={submitting}
                  />
                ) : null}
                <button type="submit" className="auth-cta" disabled={submitting}>
                  {submitting ? <span className="spinner" /> : null}
                  <span>{submitting ? 'جاري إنشاء الدعوة...' : 'إنشاء رابط الدعوة'}</span>
                </button>
              </form>

              {createdInvite ? (
                <div className="team-invite-result">
                  <div>
                    <strong>رابط الدعوة جاهز</strong>
                    <p className="tiny">
                      أرسله يدوياً إلى {createdInvite.email}. ينتهي في{' '}
                      {new Date(createdInvite.expires_at).toLocaleString('ar-SA')}.
                    </p>
                  </div>
                  <code>{createdInvite.url}</code>
                  <button type="button" className="btn-text" onClick={() => void copyInviteUrl()}>
                    {copied ? 'تم النسخ' : 'نسخ الرابط'}
                  </button>
                </div>
              ) : null}
            </section>
          ) : null}

          <div className="workspace-split">
            <section className="workspace-list card team-panel">
              <div className="sec-title">
                <h2>الدعوات</h2>
                <span className="pill">{visibleInvitations.length}</span>
              </div>
              {visibleInvitations.length === 0 ? (
                <EmptyState
                  title="لا توجد دعوات"
                  description="أنشئ دعوة جديدة وسيظهر سجلها هنا بدون إظهار التوكن."
                />
              ) : (
                <div className="row-list">
                  {visibleInvitations.map((inv) => (
                    <article key={inv.id} className="row-card team-row">
                      <div>
                        <h3>{inv.email}</h3>
                        <p className="tiny">
                          {roleLabel(inv.role)} · {inv.client?.name ?? 'دعوة وكالة'}
                        </p>
                        {inv.intended_client_assignments_count > 0 ? (
                          <p className="tiny" style={{ marginTop: 4 }}>
                            <span className="pill pill-info">
                              {inv.intended_client_assignments_count} عميل معيّن عند القبول
                            </span>
                          </p>
                        ) : null}
                      </div>
                      <div className="team-row-actions">
                        <span className={statusClass(inv.status)}>
                          {INVITATION_STATUS_LABEL[inv.status]}
                        </span>
                        {canRevoke && inv.status === 'pending' ? (
                          <button
                            type="button"
                            className="btn-text"
                            onClick={() => void onRevoke(inv.id)}
                          >
                            إلغاء
                          </button>
                        ) : null}
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>

            <AgencyEmployeeRosterPanel
              userRoles={userRoles}
              clients={clients}
              isLeader={isLeader}
              selectedEmployeeUserId={selectedEmployeeUserId}
              onSelectEmployee={setSelectedEmployeeUserId}
              onToggleClientAssignment={(uid, cid, aid) =>
                void onToggleClientAssignment(uid, cid, aid)
              }
              toggleState={toggleState}
              reloadKey={reloadKey}
            />
          </div>
        </>
      ) : null}
    </div>
  );
}

/**
 * Task 112 (TEAM-1B) — multi-select for the optional CAT-bootstrap on
 * agency-role invitations. Renders as a scrollable checkbox list; the
 * cap and the role-category gating live in the EF — this UI is the
 * usability layer. Empty selection is valid (the invitee joins as an
 * agency seat without any CAT memberships; leadership can add them
 * later via the client workspace "الفريق" tab).
 */
function AgencyClientAssignmentsPicker(props: {
  readonly clients: ReadonlyArray<ClientRow>;
  readonly selected: ReadonlySet<string>;
  readonly onChange: (next: ReadonlySet<string>) => void;
  readonly disabled?: boolean;
}): JSX.Element {
  // Only offer non-archived clients (matches the EF's validator).
  const offerable = props.clients.filter((c) => c.status !== 'archived');
  const sorted = [...offerable].sort((a, b) => a.name.localeCompare(b.name, 'ar'));
  function toggle(id: string): void {
    const next = new Set(props.selected);
    if (next.has(id)) {
      next.delete(id);
    } else {
      if (next.size >= MAX_INTENDED_CLIENT_ASSIGNMENTS) return;
      next.add(id);
    }
    props.onChange(next);
  }
  function clearAll(): void {
    if (props.selected.size === 0) return;
    props.onChange(new Set());
  }
  return (
    <fieldset
      style={{
        border: '1px solid var(--line)',
        borderRadius: 'var(--r-md)',
        padding: '12px 14px',
        margin: 0,
      }}
    >
      <legend style={{ padding: '0 6px', fontWeight: 600, fontSize: 13 }}>
        فرق العملاء (اختياري)
      </legend>
      <p className="tiny" style={{ marginTop: 4, marginBottom: 8, color: 'var(--muted)' }}>
        اختياري — يصبح عضوًا في فريق وكالة العميل لكل عميل تختاره. يمكن إضافة أو إزالة عملاء لاحقًا
        من تابة الفريق داخل مساحة العميل.
      </p>
      {sorted.length === 0 ? (
        <p className="tiny" style={{ color: 'var(--muted)' }}>
          لا يوجد عملاء متاحون.
        </p>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 6,
            maxHeight: 220,
            overflowY: 'auto',
            paddingInlineEnd: 4,
          }}
        >
          {sorted.map((c) => {
            const checked = props.selected.has(c.id);
            const reachedCap = !checked && props.selected.size >= MAX_INTENDED_CLIENT_ASSIGNMENTS;
            return (
              <label
                key={c.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: 13,
                  cursor: props.disabled || reachedCap ? 'not-allowed' : 'pointer',
                  opacity: props.disabled || reachedCap ? 0.5 : 1,
                }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={props.disabled || reachedCap}
                  onChange={() => toggle(c.id)}
                />
                <span>{c.name}</span>
              </label>
            );
          })}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 8 }}>
        <span className="tiny" style={{ color: 'var(--muted)' }}>
          {props.selected.size} / {MAX_INTENDED_CLIENT_ASSIGNMENTS} محدد
        </span>
        {props.selected.size > 0 ? (
          <button
            type="button"
            className="btn-text"
            onClick={clearAll}
            disabled={props.disabled}
            style={{ padding: '4px 10px', fontSize: 12 }}
          >
            مسح التحديد
          </button>
        ) : null}
      </div>
    </fieldset>
  );
}

function buildInvitationUrl(token: string, email: string): string {
  const origin = typeof window === 'undefined' ? '' : window.location.origin;
  const params = new URLSearchParams({ token, email });
  return `${origin}/accept-invitation?${params.toString()}`;
}

function roleLabel(role: Pick<RoleRow, 'key' | 'name_ar' | 'name_en'> | null): string {
  if (!role) return 'دور غير معروف';
  return role.name_ar || role.name_en || role.key;
}

function initials(value: string): string {
  const parts = value.split(/\s+/).filter(Boolean).slice(0, 2);
  if (parts.length === 0) return value.slice(0, 2).toUpperCase();
  return parts
    .map((p) => p.charAt(0))
    .join('')
    .toUpperCase();
}

function statusClass(status: InvitationStatus): string {
  switch (status) {
    case 'pending':
      return 'pill pill-info';
    case 'accepted':
      return 'pill pill-active';
    case 'revoked':
      return 'pill pill-paused';
    case 'expired':
      return 'pill pill-archived';
    default:
      return 'pill';
  }
}

// =====================================================================
// Task 127 — Agency-level Team & Permissions hub
// =====================================================================
//
// `AgencyEmployeeRosterPanel` replaces the previous inline "members"
// section. It switches between two views inside the same workspace-
// detail card:
//   - Grid view: the existing per-user cards. Agency-category cards
//     are clickable when the viewer is leadership; clicking sets the
//     selected employee and flips to detail view.
//   - Detail view: identity + read-only role chips + per-client
//     assignment checklist + a gated "صلاحيات إضافية" placeholder
//     section (Task 122A backend not deployed; Task 126 gate is off).
//
// The drawer uses ONLY the existing deployed EFs
// `assign-client-team-member` + `end-client-team-assignment` for
// mutations. No invitation, no `user_roles` write, no
// `user_custom_permissions` write, no 122A wrapper.

interface AgencyEmployeeRosterPanelProps {
  readonly userRoles: ReadonlyArray<UserRoleRow>;
  readonly clients: ReadonlyArray<ClientRow>;
  readonly isLeader: boolean;
  readonly selectedEmployeeUserId: string | null;
  readonly onSelectEmployee: (userId: string | null) => void;
  readonly onToggleClientAssignment: (
    userId: string,
    clientId: string,
    currentAssignmentId: string | null,
  ) => void;
  readonly toggleState: ReadonlyMap<
    string,
    { readonly busy: boolean; readonly error: string | null }
  >;
  /**
   * Task 128 — bumped by `TeamStubPage` after every successful
   * assign/remove toggle (and every other page-level refresh). Threaded
   * down to `EmployeeDetailDrawer` so its unfiltered refetch re-runs
   * after a successful mutation; without this, `unfilteredRow` keeps
   * its pre-toggle snapshot and the checklist renders stale state.
   */
  readonly reloadKey: number;
}

/**
 * Task 130 — one entry per `user_id`, carrying every role the user
 * holds. The grouping replaces the pre-Task-129 per-row render, which
 * double-counted multi-role employees because `list-user-roles` returns
 * one row per `(user_id, role_id)` pair.
 *
 * `primaryRow` is the first `UserRoleRow` encountered for the user —
 * it's used as the single representative for `profile`, `user_id`, the
 * Task 128 unfiltered-refetch matcher, and the fallback
 * `client_assignments` while the unfiltered refetch is in flight.
 * `roles` is the unique-by-`role.id` set of every role on that user
 * (insertion-order preserved; null roles skipped).
 */
type GroupedEmployeeRole = Pick<RoleRow, 'id' | 'key' | 'category' | 'name_en' | 'name_ar'>;
interface GroupedEmployee {
  readonly user_id: string;
  readonly primaryRow: UserRoleRow;
  readonly roles: ReadonlyArray<GroupedEmployeeRole>;
}

function AgencyEmployeeRosterPanel(props: AgencyEmployeeRosterPanelProps): JSX.Element {
  const { userRoles, clients, isLeader, selectedEmployeeUserId, onSelectEmployee } = props;

  // Task 130 — group `UserRoleRow`s by `user_id`. See `GroupedEmployee`
  // for the shape. The `Map` keyed by `user_id` is the load-bearing
  // de-duplicator; the inner `Set<role.id>` prevents a duplicate role
  // chip if the backend ever returns the same `(user, role)` pair twice
  // (defensive — the EF should not, but the cost of the guard is one
  // line).
  const groupedRoster = useMemo<ReadonlyArray<GroupedEmployee>>(() => {
    const byUser = new Map<
      string,
      { primaryRow: UserRoleRow; roleIds: Set<string>; roles: GroupedEmployeeRole[] }
    >();
    for (const row of userRoles) {
      let entry = byUser.get(row.user_id);
      if (!entry) {
        entry = { primaryRow: row, roleIds: new Set(), roles: [] };
        byUser.set(row.user_id, entry);
      }
      if (row.role && !entry.roleIds.has(row.role.id)) {
        entry.roleIds.add(row.role.id);
        entry.roles.push(row.role);
      }
    }
    return Array.from(byUser.values()).map((e) => ({
      user_id: e.primaryRow.user_id,
      primaryRow: e.primaryRow,
      roles: e.roles,
    }));
  }, [userRoles]);

  // Resolve the selected employee group from the current grouped
  // snapshot. If the employee fell off the roster (e.g. became
  // inactive between renders) the detail view collapses back to the
  // grid; otherwise the drawer shows the latest data.
  const selectedGroup = useMemo(() => {
    if (!selectedEmployeeUserId) return null;
    return groupedRoster.find((g) => g.user_id === selectedEmployeeUserId) ?? null;
  }, [groupedRoster, selectedEmployeeUserId]);

  // Task 127 — Codex Step A round 1 P0 follow-on: clear the page-level
  // selection when the resolved group drops out of the roster or fails
  // the active-agency gate. Without this, stale `selectedEmployeeUserId`
  // would silently linger across reloads and re-render the drawer the
  // moment the row reappears (e.g. when a leader reactivates a
  // previously-deactivated employee).
  //
  // Task 130 — the agency check now runs over the grouped `roles`
  // array (any agency role qualifies). The profile-active check
  // continues to read from the primary row.
  useEffect(() => {
    if (!selectedEmployeeUserId) return;
    if (!selectedGroup) {
      onSelectEmployee(null);
      return;
    }
    const hasAgencyRole = selectedGroup.roles.some((r) => r.category === 'agency');
    if (!hasAgencyRole || selectedGroup.primaryRow.profile?.is_active === false) {
      onSelectEmployee(null);
    }
  }, [selectedEmployeeUserId, selectedGroup, onSelectEmployee]);

  // Task 127 — Codex Step A round 1 P0: include `is_active !== false`
  // in the drawer-mount guard. A previously selected employee whose
  // profile flips inactive between renders MUST NOT keep the
  // assignment-write controls available. The grid-mode `openable`
  // filter has the same check; mirroring it here closes the race.
  //
  // Task 130 — the agency-category clause runs over the grouped roles
  // (any agency role qualifies). The drawer receives the primary row
  // (single representative) PLUS the full `roles` array for
  // multi-role chip display.
  if (
    selectedGroup &&
    isLeader &&
    selectedGroup.roles.some((r) => r.category === 'agency') &&
    selectedGroup.primaryRow.profile?.is_active !== false
  ) {
    return (
      <section className="workspace-detail card team-panel">
        <EmployeeDetailDrawer
          row={selectedGroup.primaryRow}
          roles={selectedGroup.roles}
          clients={clients}
          toggleState={props.toggleState}
          onToggleClientAssignment={props.onToggleClientAssignment}
          onClose={() => onSelectEmployee(null)}
          reloadKey={props.reloadKey}
        />
      </section>
    );
  }

  return (
    <section className="workspace-detail card team-panel">
      <div className="sec-title">
        <h2>الأعضاء الحاليون</h2>
        {/* Task 130 — count unique employees, not (user × role) rows. */}
        <span className="pill">{groupedRoster.length}</span>
      </div>
      {groupedRoster.length === 0 ? (
        <EmptyState
          title="لا يوجد أعضاء في هذا النطاق"
          description="بعد قبول الدعوة سيظهر المستخدم هنا مع دوره وتعيينات العملاء."
        />
      ) : (
        <div className="team-member-grid">
          {groupedRoster.map((group) => {
            const row = group.primaryRow;
            // Only agency-category groups open the detail drawer.
            // Non-leadership viewers see the grid unchanged (no
            // click affordance, no drawer access). Task 130 — any
            // agency role on the user qualifies; the profile-active
            // check is unchanged.
            const openable =
              isLeader &&
              group.roles.some((r) => r.category === 'agency') &&
              row.profile?.is_active !== false;
            const cardContent = (
              <>
                <div className="team-avatar">
                  {initials(row.profile?.full_name ?? row.profile?.email ?? row.user_id)}
                </div>
                <div>
                  <h3>{row.profile?.full_name ?? row.profile?.email ?? 'مستخدم'}</h3>
                  {/* Task 287B — the email is the ONLY reliable twin-account
                      disambiguator (plus-variant invites create near-identical
                      names). bidi-isolate it LTR so «+de» vs «+de1» stays
                      visually intact inside the RTL layout. */}
                  <p className="tiny" title={row.profile?.email ?? row.user_id}>
                    <span dir="ltr">{row.profile?.email ?? row.user_id}</span>
                  </p>
                  <div className="team-chip-row">
                    {/* Task 130 — one chip per role the user holds. */}
                    {group.roles.map((r) => (
                      <span key={r.id} className="pill pill-active">
                        {roleLabel(r)}
                      </span>
                    ))}
                    {row.client_assignments.map((a) => (
                      <span key={a.id} className="pill">
                        {a.client?.name ?? a.client_id.slice(0, 8)}
                      </span>
                    ))}
                  </div>
                </div>
              </>
            );
            if (openable) {
              return (
                <button
                  key={group.user_id}
                  type="button"
                  className="team-member-card team-member-card--openable"
                  onClick={() => onSelectEmployee(group.user_id)}
                  style={{
                    textAlign: 'inherit',
                    background: 'inherit',
                    border: 'inherit',
                    cursor: 'pointer',
                  }}
                  aria-label={`إدارة تعيينات العملاء للموظف ${row.profile?.full_name ?? row.profile?.email ?? row.user_id}`}
                >
                  {cardContent}
                </button>
              );
            }
            return (
              <article key={group.user_id} className="team-member-card">
                {cardContent}
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}

interface EmployeeDetailDrawerProps {
  readonly row: UserRoleRow;
  /**
   * Task 130 — every role the selected employee holds. The drawer
   * renders one chip per role in the "الدور العام" section. The
   * grouping is performed by `AgencyEmployeeRosterPanel`; the drawer
   * is given the resolved list and does NOT re-derive it from
   * `row.role` alone.
   */
  readonly roles: ReadonlyArray<GroupedEmployeeRole>;
  readonly clients: ReadonlyArray<ClientRow>;
  readonly toggleState: ReadonlyMap<
    string,
    { readonly busy: boolean; readonly error: string | null }
  >;
  readonly onToggleClientAssignment: (
    userId: string,
    clientId: string,
    currentAssignmentId: string | null,
  ) => void;
  readonly onClose: () => void;
  /**
   * Task 128 — page-level reload counter. Included in the unfiltered
   * refetch's `useEffect` dependency array so the drawer's
   * `unfilteredRow` snapshot refreshes after every successful
   * assign/remove toggle. Without this, the checklist could render
   * stale assignment state after a successful mutation.
   */
  readonly reloadKey: number;
}

function EmployeeDetailDrawer(props: EmployeeDetailDrawerProps): JSX.Element {
  const { row, roles, clients, toggleState, onToggleClientAssignment, onClose, reloadKey } = props;
  const name = row.profile?.full_name?.trim() || row.profile?.email || row.user_id;

  // Task 130 — ESC closes the drawer. Scoped to the drawer's mount
  // window: the `useEffect` registers a `document` `keydown` listener
  // and the cleanup removes it on unmount (which fires when the page
  // collapses back to grid view, e.g. via the explicit "العودة" button
  // or when the selection-clearing effect on the panel runs). The
  // handler does NOT preventDefault, does NOT add a backdrop, does
  // NOT introduce outside-click dismissal — the drawer remains an
  // in-flow panel, not a modal. The explicit "← العودة لقائمة الأعضاء"
  // button below is preserved.
  useEffect(() => {
    function handleKeydown(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        onClose();
      }
    }
    document.addEventListener('keydown', handleKeydown);
    return () => document.removeEventListener('keydown', handleKeydown);
  }, [onClose]);

  // Task 127 — Codex Step A round 4 P1: the page-level
  // `listUserRoles({ clientId: effectiveClientFilter })` narrows the
  // embedded `client_assignments` to only the filtered client's row.
  // If a leader has the scope filter set to "Client A" and opens
  // this drawer, the prop `row.client_assignments` is incomplete —
  // the checklist would render unchecked rows for Clients B/C even
  // if the employee is assigned to them, and toggling B/C ON would
  // 409 against the active-uniq partial index.
  //
  // Fix: refetch `listUserRoles({})` (UNFILTERED) when the drawer
  // mounts; pick the matching user_id row; use ITS
  // `client_assignments` for the checklist. The prop row is the
  // initial render fallback while the refetch is in flight.
  //
  // Uses an existing EF call shape (just with no client_id filter)
  // — no new EF needed.
  //
  // Task 128 — `reloadKey` is included in the dep array so the
  // unfiltered refetch ALSO re-runs after a successful assign/remove
  // toggle (which bumps `setReloadKey`). Pre-Task-128 the effect
  // depended only on `row.user_id`, so `unfilteredRow` froze at its
  // pre-toggle snapshot and the checklist rendered stale state —
  // visible as "the box doesn't move after I click it" and, worse,
  // toggling a row again would 409 against the active-uniq partial
  // index because the snapshot still pointed at a now-ended
  // assignment_id. The active-profile guard + the unfiltered refetch
  // body are unchanged from Task 127.
  const [unfilteredRow, setUnfilteredRow] = useState<UserRoleRow | null>(null);
  const [unfilteredLoading, setUnfilteredLoading] = useState(true);
  const [unfilteredError, setUnfilteredError] = useState<unknown>(null);
  useEffect(() => {
    const ctrl = new AbortController();
    setUnfilteredLoading(true);
    setUnfilteredError(null);
    listUserRoles({ signal: ctrl.signal })
      .then((data) => {
        if (ctrl.signal.aborted) return;
        const match = data.user_roles.find((r) => r.user_id === row.user_id) ?? null;
        setUnfilteredRow(match);
      })
      .catch((err: unknown) => {
        if (ctrl.signal.aborted) return;
        if (err instanceof DOMException && err.name === 'AbortError') return;
        setUnfilteredError(err);
      })
      .finally(() => {
        if (!ctrl.signal.aborted) setUnfilteredLoading(false);
      });
    return () => ctrl.abort();
  }, [row.user_id, reloadKey]);

  // Use the unfiltered row's assignments when available; fall back
  // to the prop row only during the initial load. The fallback can
  // be incomplete; the loading indicator below tells the leader to
  // wait before toggling.
  const effectiveAssignmentsRow = unfilteredRow ?? row;

  // Build a quick lookup: client_id → assignment row (if currently
  // assigned). Used by the checklist to know which clients are
  // checked + to surface the assignment_id needed for the end-EF.
  const assignmentByClientId = useMemo(() => {
    const map = new Map<string, string>();
    for (const a of effectiveAssignmentsRow.client_assignments) {
      map.set(a.client_id, a.id);
    }
    return map;
  }, [effectiveAssignmentsRow.client_assignments]);

  // Task 127 — offer every non-archived client (onboarding / active /
  // paused all qualify). Mirrors the existing
  // `AgencyClientAssignmentsPicker` filter so the agency invitation
  // surface and the per-employee detail surface stay consistent on
  // which clients are assignment-eligible. The `assign-client-team-member`
  // EF re-validates server-side via the `validate_client_assignment_scope`
  // trigger; this filter is a UX layer, not the security gate.
  // (Codex Step A round 1 P1 — explicit clarification that
  // "non-archived" is intentional vs the spec's loose phrase "active".)
  const offerable = clients
    .filter((c) => c.status !== 'archived')
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name, 'ar'));

  return (
    <div className="stack-md">
      <div className="sec-title">
        <div>
          <button
            type="button"
            className="btn-text"
            onClick={onClose}
            style={{ padding: '4px 0', marginBottom: 6 }}
            aria-label="العودة لقائمة الأعضاء"
          >
            ← العودة لقائمة الأعضاء
          </button>
          <h2 style={{ marginBottom: 4 }}>{name}</h2>
          {/* Task 287B — bidi-isolated LTR email in the drawer header too, so the
              operator can verify WHICH twin account they are assigning before
              toggling any client checkbox. */}
          <p
            className="tiny"
            style={{ color: 'var(--muted)' }}
            title={row.profile?.email ?? row.user_id}
          >
            <span dir="ltr">{row.profile?.email ?? row.user_id}</span>
          </p>
        </div>
      </div>

      {/* Read-only role chips. Task 127 explicitly forbids any role-
          mutation control here — the global role is set elsewhere and
          must NOT change as a side-effect of client assignment.
          Task 130 — one chip per role the user holds (multi-role
          employees see all chips). */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <strong style={{ fontSize: 13 }}>الدور العام</strong>
        <div className="team-chip-row">
          {roles.map((r) => (
            <span key={r.id} className="pill pill-active">
              {roleLabel(r)}
            </span>
          ))}
        </div>
        <p className="tiny" style={{ color: 'var(--muted)' }}>
          الدور العام للموظف يُدار من مكان آخر. هذه اللوحة لا تغيّره — هي تغيّر فقط العملاء الذين
          يصل إليهم هذا الموظف.
        </p>
      </div>

      {/* Per-client assignment checklist. Each row binds to the
          existing `assign-client-team-member` / `end-client-team-assignment`
          EFs. No invitation is sent, no user_roles mutation, no
          custom-permission write. */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <strong style={{ fontSize: 13 }}>تعيينات العملاء</strong>
        {unfilteredLoading ? (
          <span className="tiny" style={{ color: 'var(--muted)' }}>
            جاري تحميل التعيينات الحالية…
          </span>
        ) : null}
        {unfilteredError && !unfilteredLoading ? (
          <span className="tiny" style={{ color: 'var(--rose)' }}>
            تعذّر تحميل التعيينات الكاملة لهذا الموظف. قد تكون القائمة أدناه غير كاملة.
          </span>
        ) : null}
        {offerable.length === 0 ? (
          <p className="tiny" style={{ color: 'var(--muted)' }}>
            لا يوجد عملاء متاحون في هذه الوكالة بعد.
          </p>
        ) : (
          <ul
            style={{
              listStyle: 'none',
              margin: 0,
              padding: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
              maxHeight: 360,
              overflowY: 'auto',
            }}
          >
            {offerable.map((client) => {
              const assignmentId = assignmentByClientId.get(client.id) ?? null;
              const isAssigned = assignmentId !== null;
              const key = `${row.user_id}::${client.id}`;
              const rowState = toggleState.get(key);
              const busy = !!rowState?.busy;
              const error = rowState?.error ?? null;
              return (
                <li
                  key={client.id}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4,
                    padding: '8px 10px',
                    border: '1px solid var(--line)',
                    borderRadius: 'var(--r-sm)',
                  }}
                >
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      cursor: busy ? 'wait' : 'pointer',
                      fontSize: 13,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isAssigned}
                      // Task 127 — also disable while the unfiltered
                      // refetch is loading so the leader cannot act
                      // on the prop row's potentially-incomplete
                      // assignment list.
                      disabled={busy || unfilteredLoading}
                      onChange={() =>
                        onToggleClientAssignment(row.user_id, client.id, assignmentId)
                      }
                    />
                    <span style={{ flex: 1 }}>{client.name}</span>
                    {busy ? <span className="spinner" aria-label="جاري الحفظ…" /> : null}
                  </label>
                  {error ? (
                    <span className="tiny" style={{ color: 'var(--rose)' }}>
                      {error}
                    </span>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/*
       * Task 127 + Task 126 + Task 132 — placeholder for an eventual
       * AGENCY-LEVEL per-client extras surface on /app/team. The 122A
       * backend is now deployed AND Task 126's `TEAM_PERMISSION_OVERRIDES_UI_ENABLED`
       * flag is now `true` (un-gated by Task 132 on 2026-05-17), so
       * the per-client overrides drawer on /app/clients/<id>/team is
       * fully live. This placeholder remains because integrating the
       * override surface at the AGENCY level (here, on /app/team's
       * per-employee drawer) is a separate follow-up task — the
       * existing drawer on /app/clients/<id>/team already covers the
       * per-client manage surface for leadership.
       */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          padding: 12,
          background: 'var(--paper-2, #f8f8f6)',
          borderRadius: 'var(--r-sm)',
          border: '1px dashed var(--line)',
          opacity: 0.7,
        }}
        aria-disabled="true"
      >
        <strong style={{ fontSize: 13 }}>صلاحيات إضافية لكل عميل</strong>
        <p className="tiny" style={{ color: 'var(--muted)', margin: 0 }}>
          سيظهر هنا قريباً سطح إدارة الاستثناءات لكل عميل (Task 122A). هذا القسم غير مفعّل حالياً
          لحين نشر مكوّنات الواجهة الخلفية. سيتم تفعيله تلقائياً عند جاهزية الخادم — لا حاجة لإجراء
          من جانبك الآن.
        </p>
      </div>
    </div>
  );
}

/**
 * Task 127 — error mapping for the per-client toggle. Mirrors
 * `ClientTeamPage`'s `mutationErrorMessage` shape but trimmed to the
 * cases this drawer can actually surface (assign / end on
 * client_assignments).
 */
function toggleErrorMessage(err: unknown): string {
  if (err instanceof KmiApiError) {
    if (err.code === 'conflict') {
      // The active-uniq partial index on client_assignments rejects
      // a duplicate active row. Friendly message rather than the
      // generic conflict copy.
      const detail = typeof err.body.detail === 'string' ? err.body.detail : null;
      if (detail === 'duplicate') {
        return 'هذا الموظف مُعيّن لهذا العميل بالفعل.';
      }
      return 'تعذّر إكمال العملية بسبب تعارض في البيانات.';
    }
    if (err.code === 'permission_denied') {
      return 'لا تملك صلاحية إدارة فريق هذا العميل.';
    }
    if (err.code === 'not_found') {
      return 'العميل أو الموظف غير متاح.';
    }
    if (err.code === 'validation_error') {
      return 'تعذّر إكمال العملية: الموظف يجب أن يكون موظف وكالة نشطاً.';
    }
    if (err.code === 'unauthenticated') {
      return 'انتهت الجلسة. سيتم إعادة تسجيل الدخول…';
    }
  }
  return 'تعذّر إكمال العملية. حاول مجدداً.';
}
